/**
 * @lodex-pro/web-shared/auth/use-active-tenant — post-login tenant bootstrap.
 *
 * Quick 260614-pli — the web apps had NO tenant-selection flow: App.tsx went
 * straight from `isAuthenticated` to the shell, so any user whose
 * `users.active_tenant_id IS NULL` (the default at provisioning) logged in fine
 * but every tenant-scoped call (e.g. GET /v1/jobs) 403'd with
 * `tenant_not_selected` → "Could not load jobs." This hook closes that gap.
 *
 * Flow (runs once the user is authenticated):
 *   1. GET /v1/users/me/tenants → { memberships, activeTenantId }.
 *   2. activeTenantId != null            → 'ready' (already scoped; proceed).
 *   3. memberships.length === 0          → 'suspended' if the server says the
 *                                          company is suspended (DEC-349), else
 *                                          'revoked' if the membership was
 *                                          removed, else 'none'.
 *   4. memberships.length === 1          → auto-select it (no UI) → 'ready'.
 *   5. memberships.length > 1            → 'select' (caller renders a picker;
 *                                          calls selectTenant(id)).
 *
 * INITIAL selection is NOT a tenant SWITCH. The existing TenantPicker forces a
 * full email+TOTP re-auth on switch (Discretion #3) because the user is leaving
 * an already-active tenant. Here the user JUST logged in and has no active
 * tenant yet, so we must NOT sign them out — that would loop them back to the
 * sign-in screen. POST /v1/auth/switch-tenant flips active_tenant_id and busts
 * the server auth cache; the SAME access token then re-resolves to the new
 * tenant on the next request (switch-tenant.ts: "JWT does not need to be
 * refreshed; cache bust is sufficient"). So we just transition to 'ready'.
 *
 * Transport is `authedFetch` (NOT raw fetch): the API is Bearer-authenticated,
 * so both calls need the Authorization header + the 401→refresh→retry path.
 * (The legacy TenantPicker uses raw `fetch` with no Bearer — it was never wired
 * into a deployed app, so that latent bug never surfaced.)
 *
 * Quick 260816-vr9 — 'revoked' vs 'none'. This hook used to infer "no workspace"
 * from an EMPTY memberships list, which cannot distinguish a user who never
 * joined a tenant from one who was deactivated: `list_user_tenant_memberships`
 * filters `status = 'ACTIVE'`, so both arrive as `[]`. That is why PR #225 —
 * which added the ACCESS_REVOKED code precisely so a revoked user would stop
 * being told to pick a tenant — changed nothing a user could see: the API
 * started returning the right code while this hook kept rendering the
 * no-workspace copy from the empty array. The server now states the reason
 * outright via `accessRevoked`, and callers branch on it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { authedFetch, type WebApiOpts } from './api-client.js';
import type { TenantMembership } from './tenant-picker.js';

/** Bootstrap state machine. */
export type ActiveTenantState =
  | { kind: 'checking' }
  | { kind: 'ready' }
  | { kind: 'select'; memberships: ReadonlyArray<TenantMembership> }
  | { kind: 'none' }
  /**
   * Quick 260816-vr9 — authenticated, but the membership for this tenant is not
   * ACTIVE. Distinct from 'none' ONLY in what the user is told: 'none' says
   * "pick a tenant / ask to be added", which a revoked user cannot act on.
   */
  | { kind: 'revoked' }
  /**
   * DEC-349 (OPEN-134) — authenticated, but the company itself is suspended and
   * the user has no other company to use. The server drops suspended companies
   * from the membership list, so before this flag existed the sole-membership
   * auto-switch landed them in an empty app as "Unknown user".
   */
  | { kind: 'suspended' }
  | { kind: 'error' };

export interface UseActiveTenantResult {
  readonly state: ActiveTenantState;
  /**
   * Select a tenant from the picker (the `memberships.length > 1` branch).
   * Flips active_tenant_id server-side and transitions to 'ready' on success.
   * No sign-out — this is the first selection, not a mid-session switch.
   */
  readonly selectTenant: (tenantId: string) => Promise<void>;
}

interface MeTenantsResponse {
  readonly memberships: ReadonlyArray<TenantMembership>;
  readonly activeTenantId: string | null;
  /**
   * Quick 260816-vr9 — OPTIONAL on purpose. A web bundle can outlive the API it
   * talks to (CloudFront caches the SPA; a rollback can put an older API behind
   * a newer client), and an absent field must degrade to the previous copy
   * rather than crash or accuse a legitimate new user of being deactivated.
   * Treated as false unless explicitly true.
   */
  readonly accessRevoked?: boolean;
  /**
   * DEC-349 (OPEN-134) — OPTIONAL for the same reason as `accessRevoked`; absent
   * means false, which degrades to the pre-DEC-349 copy.
   */
  readonly tenantSuspended?: boolean;
}

/**
 * Resolve the user's active tenant after login. Pass `enabled: false` while the
 * auth gate is still loading or the user is signed out, so the hook does not
 * fire requests against an unauthenticated session.
 */
export function useActiveTenant(opts: WebApiOpts, enabled: boolean): UseActiveTenantResult {
  const [state, setState] = useState<ActiveTenantState>({ kind: 'checking' });
  // Guards against a double auto-switch under React StrictMode's double-invoke
  // and against re-entrancy if `enabled`/`opts` identity changes mid-flight.
  const ranRef = useRef(false);

  const postSwitch = useCallback(
    async (tenantId: string): Promise<boolean> => {
      const res = await authedFetch(opts, '/v1/auth/switch-tenant', {
        method: 'POST',
        body: JSON.stringify({ tenantId }),
      });
      return res.ok;
    },
    [opts],
  );

  useEffect(() => {
    if (!enabled) return;
    if (ranRef.current) return;
    ranRef.current = true;

    let mounted = true;
    void (async (): Promise<void> => {
      try {
        const res = await authedFetch(opts, '/v1/users/me/tenants');
        if (!res.ok) {
          if (mounted) setState({ kind: 'error' });
          return;
        }
        const body = (await res.json()) as MeTenantsResponse;
        if (!mounted) return;

        if (body.activeTenantId !== null) {
          setState({ kind: 'ready' });
          return;
        }
        if (body.memberships.length === 0) {
          // Quick 260816-vr9 — an empty list alone cannot tell "never joined a
          // tenant" from "was removed from one"; only the server knows which.
          // DEC-349 — suspension outranks revocation: it is company-wide, and no
          // administrator inside the company can undo it.
          setState({
            kind:
              body.tenantSuspended === true
                ? 'suspended'
                : body.accessRevoked === true
                  ? 'revoked'
                  : 'none',
          });
          return;
        }
        if (body.memberships.length === 1) {
          // Sole membership — select it silently (no picker).
          const only = body.memberships[0];
          if (only === undefined) {
            setState({ kind: 'error' });
            return;
          }
          const ok = await postSwitch(only.tenantId);
          if (!mounted) return;
          setState(ok ? { kind: 'ready' } : { kind: 'error' });
          return;
        }
        setState({ kind: 'select', memberships: body.memberships });
      } catch {
        if (mounted) setState({ kind: 'error' });
      }
    })();

    return () => {
      mounted = false;
    };
  }, [enabled, opts, postSwitch]);

  const selectTenant = useCallback(
    async (tenantId: string): Promise<void> => {
      setState({ kind: 'checking' });
      try {
        const ok = await postSwitch(tenantId);
        setState(ok ? { kind: 'ready' } : { kind: 'error' });
      } catch {
        setState({ kind: 'error' });
      }
    },
    [postSwitch],
  );

  return { state, selectTenant };
}
